/**
 * labs.service.ts
 *
 * API wrappers for the Labs domain, aligned with LabOrdersController.php.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';
import type { AccessResultStatus } from './lab-orders.service';

// ─── Types matching actual API response from byPatient/index ──────────────────

export interface LabResult {
  rowKey: string;
  labResultId: number;
  source: 'quest' | 'vibrant' | 'access';
  patientId: number;
  patientDisplayId: string | null;
  patientName: string | null;
  birthDate: string | null;
  orderedAt: string | null;
  /** Specimen collection date/time parsed from HL7 (OBR-7). Null until results arrive. */
  collectedAt: string | null;
  /** Results-reported date/time parsed from HL7 (OBR-22). Null until results arrive. */
  resultedAt: string | null;
  orderNumber: string | null;
  orderDisplay: string | null;
  testsSummary: string | null;
  orderFound: boolean;
  controlId: string | null;
  requestId: string | null;
  orcStatus: string | null;
  hasPdf: boolean;
  /** "Access Labs" on an Access row; not sent for Quest or Vibrant. */
  sourceName?: string;
  /** Access only, or null. */
  resultStatus?: AccessResultStatus | null;
}

export interface LabResultsPage {
  data: LabResult[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

export interface LabDetailFile {
  /** Document type from labs_quest_orders_files (REQ/ABN/PDF/AOEURL) or 'LABEL' for account-template specimen labels. */
  type?: string;
  name: string;
  label?: string;
  receivedAt?: string | null;
  /**
   * Fully-prefixed API URL the backend embeds for this file
   * (e.g. `/api/v5/lab-orders/quest/123/label?name=...`). Strip with
   * `downloadLabResultFile()` from lab-orders.service to honor apiClient's
   * baseURL exactly once.
   */
  url?: string | null;
  testCode?: string | null;
  aoeUrl?: string | null;
}

export interface LabDetailItem {
  testName: string;
  value: string | null;
  units: string | null;
  referenceRange: string | null;
  abnormalFlag: string | null;
  status: string | null;
}

export interface LabDetail {
  id: number;
  source: string;
  patientId: number;
  patientDisplayId: string | null;
  patientName: string | null;
  birthDate: string | null;
  orderedAt: string | null;
  orderNumber: string | null;
  testSummary: string | null;
  orderFound: boolean;
  status: string | null;
  /** Vibrant orders only: the derived delivery status (see VibrantOrder). */
  statusKey?: VibrantOrderStatusKey;
  /** Vibrant orders only: Send again is allowed for this order and this user. */
  canResend?: boolean;
  files: LabDetailFile[];
  items: LabDetailItem[];
  sections: Array<{
    title: string;
    subtitle?: string;
    content?: string;
    observations?: Array<{
      code: string;
      label: string;
      value: string;
      unit: string | null;
      referenceRange: string | null;
      flag: string | null;
    }>;
  }>;
  summary: Record<string, unknown>;
}

export interface LabDocument {
  noteId: number;
  title: string;
  source: string;
  /**
   * Document category derived from the legacy classifier
   * (LAB_RESULTS | IMAGING | GENETICS | REPORTS | OTHER). Null = unclassified.
   */
  fileType?: string | null;
  /** Result Date — the note's own date. */
  date: string | null;
  /** Collect Date — specimen collection date from the attachment XML (lab-documents only). */
  collectionDate?: string | null;
  portalVisible: boolean;
  /**
   * Soft-deleted: the note behind this document was deleted. Only ever true
   * when the list was explicitly asked for deleted documents.
   */
  deleted?: boolean;
  /** Who soft-deleted it. Null when unknown -- deletions were not recorded before Sep 2026. */
  deletedBy?: string | null;
  /** When it was deleted. Null whenever deletedBy is. */
  deletedAt?: string | null;
  attachments: Array<{
    attachmentId: number;
    filename: string;
  }>;
}

export interface LabTest {
  testTypeId: number;
  name: string;
  code: string | null;
}

// ─── Patient lab results (Quest + Vibrant from byPatient) ─────────────────────

export function getPatientLabResults(
  patientId: number,
  params: { page?: number; perPage?: number; sortBy?: string; sortDir?: string; search?: string } = {},
): Promise<LabResultsPage> {
  return apiClient
    .get<LabResultsPage>(`/patients/${patientId}/lab-orders`, { params })
    .then((r) => r.data);
}

// ─── Lab detail (Quest or Vibrant) ────────────────────────────────────────────

export function getLabDetail(source: string, id: number): Promise<LabDetail> {
  return apiClient
    .get<ApiResponse<LabDetail>>(`/lab-orders/detail/${source}/${id}`)
    .then((r) => r.data.data);
}

// ─── Downloads ────────────────────────────────────────────────────────────────

export function getLabReportUrl(source: string, id: number): string {
  return `/lab-orders/download/${source}/${id}/report`;
}

export function getLabFileUrl(source: string, id: number, filename: string): string {
  return `/lab-orders/download/${source}/${id}/file?name=${encodeURIComponent(filename)}`;
}

/**
 * Result of resolving a lab document to a viewable URL.
 * `revoke` is true when `url` is an object URL the caller must
 * `URL.revokeObjectURL()` once it is done with it (i.e. not a redirect URL).
 */
export interface LabViewUrl {
  url: string;
  revoke: boolean;
}

async function fetchLabViewUrl(path: string): Promise<LabViewUrl> {
  const separator = path.includes('?') ? '&' : '?';
  const response = await apiClient.get(`${path}${separator}inline=1`, { responseType: 'blob' });
  const blob = response.data as Blob;

  if (blob.type === 'application/json') {
    const json = JSON.parse(await blob.text()) as { redirect?: string };
    if (json.redirect) {
      return { url: json.redirect, revoke: false };
    }
  }

  return { url: URL.createObjectURL(blob), revoke: true };
}

/** Resolve a Quest/Vibrant report to a viewable URL (for inline preview). */
export function fetchLabReportViewUrl(source: string, id: number): Promise<LabViewUrl> {
  return fetchLabViewUrl(getLabReportUrl(source, id));
}

/** Resolve a note attachment to a viewable URL (for inline preview). */
export function fetchNoteAttachmentViewUrl(patientId: number, attachmentId: number): Promise<LabViewUrl> {
  return fetchLabViewUrl(`/patients/${patientId}/lab-attachments/${attachmentId}`);
}

export async function downloadLabReport(source: string, id: number): Promise<void> {
  const response = await apiClient.get(getLabReportUrl(source, id), { responseType: 'blob' });
  const blob = response.data as Blob;

  if (blob.type === 'application/json') {
    const json = JSON.parse(await blob.text()) as { redirect?: string };
    if (json.redirect) {
      const a = document.createElement('a');
      a.href = json.redirect;
      a.download = `lab_report_${source}_${id}.pdf`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `lab_report_${source}_${id}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Lab tests (for ordering) ─────────────────────────────────────────────────

export function getLabTests(): Promise<LabTest[]> {
  return apiClient
    .get<ApiResponse<LabTest[]>>('/lab-orders/tests')
    .then((r) => r.data.data);
}

// ─── Note-based lab documents ─────────────────────────────────────────────────

/**
 * The three note-based document lists. `includeDeleted` also returns documents
 * whose note was soft-deleted; the API allows that only for users holding
 * "Delete Labs" (legacy permission 23) and answers 403 otherwise, so whatever
 * sets it must be gated on the same permission.
 */
export function getPatientLabDocuments(patientId: number, includeDeleted = false): Promise<LabDocument[]> {
  return apiClient
    .get<ApiResponse<LabDocument[]>>(`/patients/${patientId}/lab-documents`, {
      params: includeDeleted ? { includeDeleted: '1' } : undefined,
    })
    .then((r) => r.data.data);
}

/** Imaging documents (notes_attachments.file_type = IMAGING). */
export function getPatientImagingDocuments(patientId: number, includeDeleted = false): Promise<LabDocument[]> {
  return apiClient
    .get<ApiResponse<LabDocument[]>>(`/patients/${patientId}/lab-documents/imaging`, {
      params: includeDeleted ? { includeDeleted: '1' } : undefined,
    })
    .then((r) => r.data.data);
}

/** Genetics documents (notes_attachments.file_type = GENETICS). */
export function getPatientGeneticsDocuments(patientId: number, includeDeleted = false): Promise<LabDocument[]> {
  return apiClient
    .get<ApiResponse<LabDocument[]>>(`/patients/${patientId}/lab-documents/genetics`, {
      params: includeDeleted ? { includeDeleted: '1' } : undefined,
    })
    .then((r) => r.data.data);
}

export function deleteLabDocument(patientId: number, noteId: number): Promise<void> {
  return apiClient.delete(`/patients/${patientId}/notes/${noteId}`).then(() => undefined);
}

/**
 * Undo the soft delete above. Shared with the patient Notes section: a lab
 * document is a note, so deleting one only sets notes.active = 0.
 */
export function restoreLabDocument(patientId: number, noteId: number): Promise<void> {
  return apiClient.post(`/patients/${patientId}/notes/${noteId}/restore`).then(() => undefined);
}

// ─── Lab-order notes (notes_types.isLab = 2) ──────────────────────────────────

export function getPatientLabOrderNotes(patientId: number): Promise<LabDocument[]> {
  return apiClient
    .get<ApiResponse<LabDocument[]>>(`/patients/${patientId}/lab-order-notes`)
    .then((r) => r.data.data);
}

// ─── Vibrant outbound orders (labs_vibrant_orders) ────────────────────────────

/**
 * A Vibrant order's delivery status as staff see it, derived by the API from
 * whether Vibrant's order summary has come back (VibrantOrderStatus).
 */
export type VibrantOrderStatusKey =
  | 'received'
  | 'failed'
  | 'awaiting'
  | 'unconfirmed'
  | 'legacy-sent'
  | 'legacy-pending';

export interface VibrantOrder {
  orderId: number;
  accessionId: string | null;
  testsSummary: string | null;
  collectionDate: string | null;
  statusKey: VibrantOrderStatusKey;
  /** Plain-language status, the same words the order detail shows. */
  statusLabel: string;
  /** Send again is allowed for this order and this user. */
  canResend: boolean;
  /** Raw delivery column; display statusLabel instead. */
  deliveryStatus: string | null;
  /** Plain-language reason a failed order was not sent; null otherwise. */
  deliveryProblem: string | null;
  createDate: string | null;
  sentDate: string | null;
}

export function getPatientVibrantOrders(patientId: number): Promise<VibrantOrder[]> {
  return apiClient
    .get<ApiResponse<VibrantOrder[]>>(`/patients/${patientId}/vibrant-orders`)
    .then((r) => r.data.data);
}

// ─── Biomarkers (server-side HL7 parsed results) ─────────────────────────────

export interface BiomarkerResult {
  testName: string;
  value: string;
  unit: string | null;
  referenceRange: string | null;
  source: string;
  date: string;
  resultId: number;
  resultSource: string;
}

export function getPatientBiomarkers(patientId: number): Promise<BiomarkerResult[]> {
  return apiClient
    .get<ApiResponse<BiomarkerResult[]>>(`/patients/${patientId}/lab-biomarkers`)
    .then((r) => r.data.data);
}

// ─── Note attachment download ─────────────────────────────────────────────────

export async function downloadNoteAttachment(patientId: number, attachmentId: number, filename: string): Promise<void> {
  const response = await apiClient.get(`/patients/${patientId}/lab-attachments/${attachmentId}`, { responseType: 'blob' });
  const blob = response.data as Blob;

  if (blob.type === 'application/json') {
    const json = JSON.parse(await blob.text()) as { redirect?: string };
    if (json.redirect) {
      const a = document.createElement('a');
      a.href = json.redirect;
      a.download = filename || 'attachment';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'attachment';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Note attachment download diagnostics (level 5) ───────────────────────────

export interface LabDebugCall {
  url: string;
  status: number;
  body: string;
}

export interface LabDebugResult {
  patient: LabDebugCall;
  staff: LabDebugCall;
}

/**
 * Level-5 diagnostic: hits the real v4 file-download endpoints with ?debug=1
 * (both the patient and staff routes) and returns each call's raw status + body.
 */
export async function fetchLabAttachmentDebug(
  patientId: number,
  noteId: number,
  attachmentId: number,
): Promise<LabDebugResult> {
  const res = await apiClient.get(`/patients/${patientId}/lab-attachments/${attachmentId}/debug`, {
    params: { noteId },
  });
  return (res.data?.data ?? res.data) as LabDebugResult;
}

// ─── Quest lab order submission (legacy parity via proxy) ─────────────────────

export interface QuestOrderNote {
  text: string;
  type: 'I' | 'R';
}

export interface QuestOrderTest {
  testCode: string;
  collectionDate: string;
  obrNotes: QuestOrderNote[];
  /** Optional human label (used by Vibrant preset saving; ignored by Quest). */
  description?: string;
}

export interface QuestOrderInsurance {
  billingType: string; // 'C' | 'P' | 'T'
  insuranceId: string; // "<legal_entity>_<billing_id>"
  insuranceName: string;
  policyNumber: string;
  groupNumber: string;
  coStreet: string;
  coCity: string;
  coState: string;
  coZip: string;
  relationship: string;
  guarantorFirstName: string;
  guarantorMiddleName: string;
  guarantorLastName: string;
  guarantorAddress: string;
  guarantorCity: string;
  guarantorState: string;
  guarantorZip: string;
  guarantorPhone: string;
  insuredFirstName: string;
  insuredMiddleName: string;
  insuredLastName: string;
  insuredAddress: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
}

export interface QuestOrderPayload {
  patientId: number;
  facility: string;
  provider: string;
  businessUnit: string;
  pscHold: boolean;
  firstName: string;
  middleName: string;
  lastName: string;
  birthMonth: string;
  birthDay: string;
  birthYear: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  /** The modal's Email field, sent to the lab on the requisition. It is NEVER
   *  written back to the patient record — see demographicsFill. */
  email?: string;
  gender: string;
  /**
   * The only demographics a lab order may write back to the patient, and only
   * to fill a gap: birthDate, address/address2/city/state/zip, gender. Built by
   * LabOrderModalCore.buildDemographicsFill(), which includes a field only when
   * it was blank on the patient record when the modal opened — so an existing
   * answer is never overwritten. Name, e-mail and phone are excluded on purpose:
   * e-mail and phone are login identity and carry verification state, and
   * demographics are edited in one place, the patient profile. Null = nothing
   * to fill.
   */
  demographicsFill?: Record<string, string> | null;
  /**
   * The Female/Male picked for THIS order when the chart's gender is neither
   * ("Other") and the vendor takes the order's sex (Axiom, SCRUM-1873). Never
   * written to the chart. Absent otherwise.
   */
  orderSex?: 'F' | 'M';
  /** Vibrant only: selected location (aa_accounts_vibrant.routingId). */
  routingId?: string;
  tests: QuestOrderTest[];
  pidNotes: QuestOrderNote[];
  /** Legacy single-line internal note (matches lab-quest-handling.js line 871). */
  pidInternalNote?: string;
  /** Legacy single-line external note (matches lab-quest-handling.js line 872). */
  pidExternalNote?: string;
  insurance: QuestOrderInsurance[];
  diagnoses: string[];
  internalNotes: string[];
  externalNotes: string[];
  /** Ship-to-patient integrations: who receives the package (defaults to the
   *  patient; editable — e.g. a spouse or front desk). */
  recipientName?: string;
  /** Saved patient_shipping_addresses row the staff picked, if any. */
  shippingAddressId?: number | null;
  /** The vendor-supplied Ship to choice picked (e.g. Partell's "clinic"), if any. */
  shipToChoice?: string | null;
}

export interface QuestSubmitResult {
  /** Raw legacy response string: "<orderID>|<msg>^..." or "error|<reasons>". */
  response: string;
  success: boolean;
}

export interface QuestOrderResultLine {
  isError: boolean;
  orderId: string;
  message: string;
}

/**
 * Parse the legacy submit response into per-order result lines, matching the
 * legacy lab-quest-handling.js parsing ("^"-delimited, then "|").
 *
 * Also recognizes V4-internal JSON error bodies (e.g.
 * `{"errorCode":"INTERNAL_AUTH_ERROR","details":"..."}`) that escape through
 * the forward proxy. Without this, a JSON blob with no `^` or `|` would be
 * silently treated as a successful order id, producing a fake "Order
 * Submitted" UI on a real failure.
 */
export function parseQuestOrderResponse(response: string): QuestOrderResultLine[] {
  const raw = String(response ?? '').trim();
  if (raw === '') return [];

  if (raw.startsWith('{') || raw.startsWith('[')) {
    try {
      const decoded = JSON.parse(raw) as Record<string, unknown> | unknown;
      if (decoded && typeof decoded === 'object' && !Array.isArray(decoded)) {
        const obj = decoded as Record<string, unknown>;
        const errorCode = typeof obj.errorCode === 'string' ? obj.errorCode : '';
        const detailsField = typeof obj.details === 'string' ? obj.details : '';
        const messageField = typeof obj.message === 'string' ? obj.message : '';
        const detailParts = [errorCode, detailsField || messageField].filter((p) => p !== '');
        if (detailParts.length > 0) {
          return [{ isError: true, orderId: '', message: detailParts.join(': ') }];
        }
      }
    } catch {
      // Fall through to legacy parser if not valid JSON.
    }
  }

  return raw
    .split('^')
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk !== '')
    .map((chunk) => {
      const [head, ...rest] = chunk.split('|');
      const message = rest.join('|');
      if (head === 'error') {
        return { isError: true, orderId: '', message };
      }
      return { isError: false, orderId: head, message };
    });
}

/**
 * Throw a usable error if the V5 wrapper reports `success: false` (V4
 * forward returned a non-2xx that the controller would otherwise have
 * downgraded to a 200). Once API_v5 propagates V4 errors as real HTTP
 * errors this branch is mostly defensive, but it also catches the legacy
 * 200/success:false shape that older API_v5 deployments may still emit.
 */
function throwIfQuestForwardFailure(result: QuestSubmitResult): QuestSubmitResult {
  if (result && result.success === false) {
    const lines = parseQuestOrderResponse(result.response ?? '');
    const errorLine = lines.find((l) => l.isError);
    const message = errorLine?.message?.trim() || 'Lab service rejected the request.';
    throw new Error(message);
  }
  return result;
}

export function submitQuestOrder(order: QuestOrderPayload): Promise<QuestSubmitResult> {
  return apiClient
    .post<ApiResponse<QuestSubmitResult>>('/lab-orders', order)
    .then((r) => throwIfQuestForwardFailure(r.data.data));
}

export function saveQuestPreset(
  order: QuestOrderPayload & { presetTitle: string; presetShared: string },
): Promise<QuestSubmitResult> {
  return apiClient
    .post<ApiResponse<QuestSubmitResult>>('/lab-orders/presets', order)
    .then((r) => throwIfQuestForwardFailure(r.data.data));
}

export interface QuestPresetItem {
  type: 'preset' | 'order';
  id: number;
  value: string;
  title: string;
  shared: boolean;
}

export function getQuestPresets(
  patientId: number,
): Promise<{ presets: QuestPresetItem[]; orders: QuestPresetItem[] }> {
  return apiClient
    .get<ApiResponse<{ presets: QuestPresetItem[]; orders: QuestPresetItem[] }>>('/lab-orders/presets', {
      params: { patientId },
    })
    .then((r) => r.data.data);
}

export function getQuestPresetContent(
  type: string,
  id: number,
): Promise<{ type: string; fullOrderContent: string; title: string }> {
  return apiClient
    .get<ApiResponse<{ type: string; fullOrderContent: string; title: string }>>(
      `/lab-orders/presets/${type}/${id}`,
    )
    .then((r) => r.data.data);
}

export function resubmitQuestOrder(orderId: number, patientId: number): Promise<QuestSubmitResult> {
  return apiClient
    .post<ApiResponse<QuestSubmitResult>>(`/lab-orders/${orderId}/resubmit`, { patientId })
    .then((r) => throwIfQuestForwardFailure(r.data.data));
}

/**
 * Mark a pending Quest order's AOE questionnaire as viewed (legacy
 * lab_quest_aoeviewed.php parity). The backend sets aoeLoaded='1' so the
 * resubmit affordance is revealed only after the questionnaire was opened.
 */
export function markQuestAoeViewed(orderId: number): Promise<void> {
  return apiClient.post(`/lab-orders/${orderId}/aoe-viewed`).then(() => undefined);
}

// ─── Quest ordering support endpoints ─────────────────────────────────────────

export interface QuestFacility {
  accountQuestId: number;
  sendingFacility: string;
  facilityId: number;
  facilityName: string;
}

export interface QuestProvider {
  accountQuestProviderId: number;
  providerId: string;
  userId: number;
  providerName: string;
}

export interface QuestInsurance {
  legalEntity: string;
  billingId: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  value: string;
  label: string;
}

export interface QuestDiagnosis {
  code: string;
  title: string;
  label: string;
}

export interface QuestTestCode {
  testCode: string;
  description: string;
  specimenType: string;
  performingSite: string;
  state: string;
  label: string;
  /** Vibrant only: the location this code belongs to. */
  routingId?: string;
  location?: string;
}

export function getQuestFacilities(): Promise<QuestFacility[]> {
  return apiClient.get<ApiResponse<QuestFacility[]>>('/lab-orders/facilities').then((r) => r.data.data);
}

export function getQuestProviders(): Promise<QuestProvider[]> {
  return apiClient.get<ApiResponse<QuestProvider[]>>('/lab-orders/providers').then((r) => r.data.data);
}

export function searchQuestTests(query: string): Promise<QuestTestCode[]> {
  return apiClient.get<ApiResponse<QuestTestCode[]>>('/lab-orders/test-search', { params: { search: query } }).then((r) => r.data.data);
}

// ─── Quest admin: accounts (aa_accounts_quest) ────────────────────────────────
// Manage-side CRUD for Admin > Integrations > Quest. Credentials are inherited
// server-side and never sent from the client; sending facility is immutable once
// created (only businessUnit + facilityId are editable on update).

export interface QuestAccountRow {
  id: number;
  sendingFacility: string;
  businessUnit: string;
  facilityId: number;
  facilityName: string;
}

export interface QuestAccountInput {
  sendingFacility: string;
  businessUnit: string;
  facilityId: number;
}

export function listQuestAccountsAdmin(): Promise<QuestAccountRow[]> {
  return apiClient.get<ApiResponse<QuestAccountRow[]>>('/lab-orders/accounts').then((r) => r.data.data);
}

export function createQuestAccount(payload: QuestAccountInput): Promise<{ id: number }> {
  return apiClient.post<ApiResponse<{ id: number }>>('/lab-orders/accounts', payload).then((r) => r.data.data);
}

/** Sending facility is immutable, so update only carries businessUnit + facilityId. */
export function updateQuestAccount(id: number, payload: Omit<QuestAccountInput, 'sendingFacility'>): Promise<{ success: boolean }> {
  return apiClient.put<ApiResponse<{ success: boolean }>>(`/lab-orders/accounts/${id}`, payload).then((r) => r.data.data);
}

export function deleteQuestAccount(id: number): Promise<void> {
  return apiClient.delete(`/lab-orders/accounts/${id}`).then(() => undefined);
}

// ─── Quest admin: ordering providers (aa_accounts_quest_providers) ────────────

export interface QuestProviderRow {
  id: number;
  providerId: string;
  userId: number;
  providerName: string;
}

export interface QuestProviderInput {
  providerId: string;
  userId: number;
}

/** Reuses the read endpoint (returns accountQuestProviderId) and normalizes to `id`. */
export function listQuestProvidersAdmin(): Promise<QuestProviderRow[]> {
  return apiClient.get<ApiResponse<QuestProvider[]>>('/lab-orders/providers').then((r) =>
    r.data.data.map((p) => ({
      id: p.accountQuestProviderId,
      providerId: p.providerId,
      userId: p.userId,
      providerName: p.providerName,
    })),
  );
}

export function createQuestProvider(payload: QuestProviderInput): Promise<{ id: number }> {
  return apiClient.post<ApiResponse<{ id: number }>>('/lab-orders/providers', payload).then((r) => r.data.data);
}

export function updateQuestProvider(id: number, payload: QuestProviderInput): Promise<{ success: boolean }> {
  return apiClient.put<ApiResponse<{ success: boolean }>>(`/lab-orders/providers/${id}`, payload).then((r) => r.data.data);
}

export function deleteQuestProvider(id: number): Promise<void> {
  return apiClient.delete(`/lab-orders/providers/${id}`).then(() => undefined);
}

// ─── Vibrant outbound ordering (native, SFTP) ─────────────────────────────────
// Parallel to Quest, but delivered natively by VibrantOrderService (no legacy
// proxy). Test catalog is the admin-managed labs_vibrant_ordcode table. The
// search response reuses the QuestTestCode shape so the shared order modal can
// consume either vendor unchanged.

export interface VibrantOrderTest {
  testCode: string;
  collectionDate: string;
  description?: string;
  obrNotes: QuestOrderNote[];
}

export interface VibrantOrderPayload {
  patientId: number;
  firstName: string;
  middleName: string;
  lastName: string;
  birthMonth: string;
  birthDay: string;
  birthYear: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  gender: string;
  collectionDate: string;
  /** Selected Vibrant Provider ID (ORC-12.1 / OBR-16); name resolved server-side. */
  providerId?: string;
  /** Selected location (aa_accounts_vibrant.routingId) — MSH-6 / ORC-17. */
  routingId?: string;
  tests: VibrantOrderTest[];
  pidNotes: QuestOrderNote[];
  internalNotes: string[];
  externalNotes: string[];
}

export interface VibrantSubmitResult {
  orderId: number;
  /** Plain-language outcome to show staff as-is. */
  message: string;
  success: boolean;
}

/** A Vibrant ordering location (one per aa_accounts_vibrant.routingId). */
export interface VibrantLocation {
  routingId: string;
  routingTitle: string;
}

export function getVibrantLocations(): Promise<VibrantLocation[]> {
  return apiClient.get<ApiResponse<VibrantLocation[]>>('/vibrant-orders/locations').then((r) => r.data.data);
}

/**
 * Search orderable Vibrant codes. With a routingId: that location's own codes
 * plus the codes orderable everywhere (routingId ''). With `globalOnly`: only
 * the everywhere codes, for a preset that covers all locations.
 */
export function searchVibrantTests(query: string, routingId?: string, options?: { globalOnly?: boolean }): Promise<QuestTestCode[]> {
  return apiClient
    .get<ApiResponse<QuestTestCode[]>>('/vibrant-orders/test-search', {
      params: {
        search: query,
        ...(routingId ? { routingId } : {}),
        ...(options?.globalOnly ? { globalOnly: 1 } : {}),
      },
    })
    .then((r) => r.data.data);
}

export function getVibrantProviders(routingId?: string): Promise<QuestProvider[]> {
  return apiClient
    .get<ApiResponse<QuestProvider[]>>('/vibrant-orders/providers', {
      params: { ...(routingId ? { routingId } : {}) },
    })
    .then((r) => r.data.data);
}

export function submitVibrantOrder(order: VibrantOrderPayload): Promise<VibrantSubmitResult> {
  return apiClient
    .post<ApiResponse<VibrantSubmitResult>>('/vibrant-orders', order)
    .then((r) => r.data.data);
}

/**
 * Send an unconfirmed or failed order to Vibrant again, unchanged. Every
 * refusal, and a send that fails, rejects with a 422 carrying a plain message.
 */
export function resendVibrantOrder(orderId: number): Promise<VibrantSubmitResult> {
  return apiClient
    .post<ApiResponse<VibrantSubmitResult>>(`/vibrant-orders/${orderId}/resend`)
    .then((r) => r.data.data);
}

// ─── Vibrant admin: test-code catalog (labs_vibrant_ordcode) ──────────────────

export interface VibrantCatalogTest {
  id: number;
  testCode: string;
  description: string;
  specimenType: string;
  performingSite: string;
  state: string;
  routingId: string;
  location: string;
  active: boolean;
  /** Global rows (accountID = 0) are read-only for practice admins. */
  global: boolean;
}

export interface VibrantTestInput {
  testCode: string;
  description: string;
  specimenType?: string;
  performingSite?: string;
  state?: string;
  routingId?: string;
  active?: boolean;
}

export function listVibrantTests(): Promise<VibrantCatalogTest[]> {
  return apiClient.get<ApiResponse<VibrantCatalogTest[]>>('/vibrant-orders/tests').then((r) => r.data.data);
}

export function createVibrantTest(payload: VibrantTestInput): Promise<{ id: number }> {
  return apiClient.post<ApiResponse<{ id: number }>>('/vibrant-orders/tests', payload).then((r) => r.data.data);
}

export function updateVibrantTest(id: number, payload: VibrantTestInput): Promise<{ success: boolean }> {
  return apiClient.put<ApiResponse<{ success: boolean }>>(`/vibrant-orders/tests/${id}`, payload).then((r) => r.data.data);
}

export function deleteVibrantTest(id: number): Promise<void> {
  return apiClient.delete(`/vibrant-orders/tests/${id}`).then(() => undefined);
}

// ─── Vibrant admin: ordering providers (labs_vibrant_providers) ───────────────

export interface VibrantProviderRow {
  id: number;
  providerId: string;
  userId: number;
  providerName: string;
  locations: VibrantLocation[];
  active: boolean;
}

export interface VibrantProviderInput {
  providerId: string;
  userId: number;
  routingIds: string[];
}

export function listVibrantProvidersAdmin(): Promise<VibrantProviderRow[]> {
  return apiClient.get<ApiResponse<VibrantProviderRow[]>>('/vibrant-orders/providers/manage').then((r) => r.data.data);
}

export function createVibrantProvider(payload: VibrantProviderInput): Promise<{ id: number }> {
  return apiClient.post<ApiResponse<{ id: number }>>('/vibrant-orders/providers', payload).then((r) => r.data.data);
}

export function updateVibrantProvider(id: number, payload: VibrantProviderInput): Promise<{ success: boolean }> {
  return apiClient.put<ApiResponse<{ success: boolean }>>(`/vibrant-orders/providers/${id}`, payload).then((r) => r.data.data);
}

export function deleteVibrantProvider(id: number): Promise<void> {
  return apiClient.delete(`/vibrant-orders/providers/${id}`).then(() => undefined);
}

export function searchInsuranceProviders(query: string): Promise<QuestInsurance[]> {
  return apiClient.get<ApiResponse<QuestInsurance[]>>('/lab-orders/insurance', { params: { search: query } }).then((r) => r.data.data);
}

export function searchDiagnoses(query: string): Promise<QuestDiagnosis[]> {
  return apiClient.get<ApiResponse<QuestDiagnosis[]>>('/lab-orders/diagnoses', { params: { search: query } }).then((r) => r.data.data);
}

// ─── Vibrant presets (location-scoped; mirrors the Quest preset functions) ────

export function getVibrantPresets(
  patientId: number,
  routingId?: string,
): Promise<{ presets: QuestPresetItem[]; orders: QuestPresetItem[] }> {
  return apiClient
    .get<ApiResponse<{ presets: QuestPresetItem[]; orders: QuestPresetItem[] }>>('/vibrant-orders/presets', {
      params: { patientId, ...(routingId ? { routingId } : {}) },
    })
    .then((r) => r.data.data);
}

export interface VibrantPresetContent {
  type: string;
  fullOrderContent: string;
  title: string;
  routingId?: string;
  /**
   * Stored tests left out of `fullOrderContent` because they can no longer be
   * ordered (retired, or moved to another location since the preset or order
   * was saved).
   */
  unavailableTests?: Array<{ testCode: string; description: string }>;
}

export function getVibrantPresetContent(
  type: string,
  id: number,
): Promise<VibrantPresetContent> {
  return apiClient
    .get<ApiResponse<VibrantPresetContent>>(
      `/vibrant-orders/presets/${type}/${id}`,
    )
    .then((r) => r.data.data);
}

export interface VibrantPresetSavePayload {
  presetTitle: string;
  presetShared: string;
  routingId: string;
  tests: Array<{ testCode: string; description: string; obrNotes: QuestOrderNote[] }>;
  /** Refuse (422 DUPLICATE_TITLE) instead of replacing a preset with the same name. */
  createOnly?: boolean;
}

export function saveVibrantPreset(payload: VibrantPresetSavePayload): Promise<{ id: number }> {
  return apiClient.post<ApiResponse<{ id: number }>>('/vibrant-orders/presets', payload).then((r) => r.data.data);
}

// ─── Vibrant admin: preset management (labs_vibrant_presets) ──────────────────

export interface VibrantPresetRow {
  id: number;
  title: string;
  routingId: string;
  location: string;
  shared: boolean;
  testCount: number;
  owned: boolean;
}

export interface VibrantPresetUpdateInput {
  title: string;
  shared: string;
  /** Sent together with `tests` to replace the preset's location and test list. */
  routingId?: string;
  tests?: Array<{ testCode: string; description: string; obrNotes: QuestOrderNote[] }>;
}

export function listVibrantPresetsAdmin(): Promise<VibrantPresetRow[]> {
  return apiClient.get<ApiResponse<VibrantPresetRow[]>>('/vibrant-orders/presets/manage').then((r) => r.data.data);
}

export function updateVibrantPreset(id: number, payload: VibrantPresetUpdateInput): Promise<{ success: boolean }> {
  return apiClient.put<ApiResponse<{ success: boolean }>>(`/vibrant-orders/presets/${id}`, payload).then((r) => r.data.data);
}

export function deleteVibrantPreset(id: number): Promise<void> {
  return apiClient.delete(`/vibrant-orders/presets/${id}`).then(() => undefined);
}

// ─── Access Labs ordering setup (/access-orders/*) ───────────────────────────
// A copy of the Vibrant block for Access's tables (AccessOrdersController).
// Differences: test search is capped and says so (meta.capped), and the admin
// test list is searched and paged on the server, because Access's compendium
// is ~16,000 codes. Compendium rows (accountID 0, `global`) are read-only.
// Refusals are 422 (404 for a row that is not the practice's) with a code;
// writes are 403 MODULE_DISABLED while the Access Labs module is off.

/** An Access ordering location (one per open aa_accounts_access.routingId). */
export interface AccessLocation {
  routingId: string;
  routingTitle: string;
}

export function getAccessLocations(): Promise<AccessLocation[]> {
  return apiClient.get<ApiResponse<AccessLocation[]>>('/access-orders/locations').then((r) => r.data.data);
}

export interface AccessTestSearchResult {
  tests: QuestTestCode[];
  /** More codes matched than were returned (at most `cap`). */
  capped: boolean;
  cap: number;
}

/**
 * Search orderable Access codes. With a routingId: that location's own codes
 * plus the codes orderable everywhere. With `globalOnly`: only the codes with
 * no location, for a preset that covers all locations.
 */
export function searchAccessTests(
  query: string,
  routingId?: string,
  options?: { globalOnly?: boolean },
): Promise<AccessTestSearchResult> {
  return apiClient
    .get<ApiResponse<QuestTestCode[]>>('/access-orders/test-search', {
      params: {
        search: query,
        ...(routingId ? { routingId } : {}),
        ...(options?.globalOnly ? { globalOnly: 1 } : {}),
      },
    })
    .then((r) => ({
      tests: r.data.data,
      capped: r.data.meta?.capped === true,
      cap: typeof r.data.meta?.cap === 'number' ? r.data.meta.cap : r.data.data.length,
    }));
}

export function getAccessProviders(routingId?: string): Promise<QuestProvider[]> {
  return apiClient
    .get<ApiResponse<QuestProvider[]>>('/access-orders/providers', {
      params: { ...(routingId ? { routingId } : {}) },
    })
    .then((r) => r.data.data);
}

// ─── Access orders (labs_access_orders) ───────────────────────────────────────
// Placing an order saves it, builds the HL7 and uploads it. An upload that
// fails leaves the order saved and "failed": the submit answers 502
// ORDER_NOT_SENT with details.orderId, and the order can be resent unchanged
// (KTD15). An upload whose last step got no reply answers 502
// ORDER_NOT_CONFIRMED and leaves the order "unconfirmed": Access may have it.
// Refusals before anything is saved are 422 with a code.

/** The Access order form's payload: VibrantOrderPayload's shape. */
export type AccessOrderPayload = VibrantOrderPayload;

export interface AccessSubmitResult {
  orderId: number;
  fileName: string;
  message: string;
  success: boolean;
}

export function submitAccessOrder(order: AccessOrderPayload): Promise<AccessSubmitResult> {
  return apiClient.post<ApiResponse<AccessSubmitResult>>('/access-orders', order).then((r) => r.data.data);
}

/**
 * Send a not-sent order again under the same order id, with nothing re-entered.
 * An unconfirmed order (Access may have it) is refused with
 * ORDER_MAY_HAVE_ARRIVED unless staff confirm Access did not receive it.
 */
export function resendAccessOrder(orderId: number, confirmNotReceived = false): Promise<AccessSubmitResult> {
  return apiClient
    .post<ApiResponse<AccessSubmitResult>>(`/access-orders/${orderId}/resend`, confirmNotReceived ? { confirmNotReceived: true } : undefined)
    .then((r) => r.data.data);
}

/**
 * A patient's Access order, as GET /patients/{id}/access-orders returns it.
 * Its own shape: the Vibrant list now derives its status, and this one does not.
 */
export interface AccessOrder {
  orderId: number;
  accessionId: string | null;
  testsSummary: string | null;
  collectionDate: string | null;
  deliveryStatus: string | null;
  /** Staff-safe text explaining a failed send; null otherwise. */
  deliveryError: string | null;
  remoteFileName: string | null;
  createDate: string | null;
  sentDate: string | null;
  /** Not sent, and saved in full, so Resend can send it again. */
  canResend: boolean;
  /** Placed moments ago and may still reach Access, so it cannot be resent yet. */
  sending?: boolean;
  /** The last send got no reply: Access may have it, so staff check before resending. */
  unconfirmed?: boolean;
}

export function getPatientAccessOrders(patientId: number): Promise<AccessOrder[]> {
  return apiClient
    .get<ApiResponse<AccessOrder[]>>(`/patients/${patientId}/access-orders`)
    .then((r) => r.data.data);
}

// ─── Access admin: test-code catalog (labs_access_ordcode) ────────────────────

export interface AccessCatalogTest {
  id: number;
  testCode: string;
  /** Empty for the compendium codes Access sent with no name. */
  description: string;
  specimenType: string;
  performingSite: string;
  state: string;
  routingId: string;
  location: string;
  active: boolean;
  /** Compendium rows (accountID = 0) are read-only for practice admins. */
  global: boolean;
}

export interface AccessTestInput {
  testCode: string;
  description: string;
  specimenType?: string;
  performingSite?: string;
  state?: string;
  routingId?: string;
  active?: boolean;
}

export interface AccessTestListParams {
  search: string;
  page: number;
  perPage: number;
  /** Only the practice's own codes, leaving out the compendium. */
  practiceOnly?: boolean;
}

export interface AccessTestPage {
  tests: AccessCatalogTest[];
  page: number;
  perPage: number;
  total: number;
  hasMore: boolean;
}

/** One page of the Access catalog for the Admin Tests tab, searched on the server. */
export function listAccessTests(params: AccessTestListParams): Promise<AccessTestPage> {
  return apiClient
    .get<ApiResponse<AccessCatalogTest[]>>('/access-orders/tests', {
      params: {
        search: params.search,
        page: params.page,
        perPage: params.perPage,
        ...(params.practiceOnly ? { practiceOnly: 1 } : {}),
      },
    })
    .then((r) => {
      const meta = r.data.meta ?? {};
      const num = (v: unknown, fallback: number) => (typeof v === 'number' ? v : fallback);
      return {
        tests: r.data.data,
        page: num(meta.page, params.page),
        perPage: num(meta.perPage, params.perPage),
        total: num(meta.total, r.data.data.length),
        hasMore: meta.hasMore === true,
      };
    });
}

export function createAccessTest(payload: AccessTestInput): Promise<{ id: number }> {
  return apiClient.post<ApiResponse<{ id: number }>>('/access-orders/tests', payload).then((r) => r.data.data);
}

export function updateAccessTest(id: number, payload: AccessTestInput): Promise<{ success: boolean }> {
  return apiClient.put<ApiResponse<{ success: boolean }>>(`/access-orders/tests/${id}`, payload).then((r) => r.data.data);
}

export function deleteAccessTest(id: number): Promise<void> {
  return apiClient.delete(`/access-orders/tests/${id}`).then(() => undefined);
}

// ─── Access admin: ordering providers (labs_access_providers) ─────────────────

export interface AccessProviderRow {
  id: number;
  providerId: string;
  userId: number;
  providerName: string;
  locations: AccessLocation[];
  active: boolean;
}

export interface AccessProviderInput {
  providerId: string;
  userId: number;
  routingIds: string[];
}

export function listAccessProvidersAdmin(): Promise<AccessProviderRow[]> {
  return apiClient.get<ApiResponse<AccessProviderRow[]>>('/access-orders/providers/manage').then((r) => r.data.data);
}

export function createAccessProvider(payload: AccessProviderInput): Promise<{ id: number }> {
  return apiClient.post<ApiResponse<{ id: number }>>('/access-orders/providers', payload).then((r) => r.data.data);
}

export function updateAccessProvider(id: number, payload: AccessProviderInput): Promise<{ success: boolean }> {
  return apiClient.put<ApiResponse<{ success: boolean }>>(`/access-orders/providers/${id}`, payload).then((r) => r.data.data);
}

export function deleteAccessProvider(id: number): Promise<void> {
  return apiClient.delete(`/access-orders/providers/${id}`).then(() => undefined);
}

// ─── Access presets (location-scoped, as on Vibrant) ──────────────────────────

export function getAccessPresets(
  patientId: number,
  routingId?: string,
): Promise<{ presets: QuestPresetItem[]; orders: QuestPresetItem[] }> {
  return apiClient
    .get<ApiResponse<{ presets: QuestPresetItem[]; orders: QuestPresetItem[] }>>('/access-orders/presets', {
      params: { patientId, ...(routingId ? { routingId } : {}) },
    })
    .then((r) => r.data.data);
}

export interface AccessPresetContent {
  type: string;
  fullOrderContent: string;
  title: string;
  routingId?: string;
  /** Stored tests left out of `fullOrderContent` because they can no longer be ordered. */
  unavailableTests?: Array<{ testCode: string; description: string }>;
}

export function getAccessPresetContent(type: string, id: number): Promise<AccessPresetContent> {
  return apiClient
    .get<ApiResponse<AccessPresetContent>>(`/access-orders/presets/${type}/${id}`)
    .then((r) => r.data.data);
}

/** One test of an Access preset, as saved or replaced. */
export type AccessPresetTest = { testCode: string; description: string; obrNotes: QuestOrderNote[] };

export interface AccessPresetSavePayload {
  presetTitle: string;
  presetShared: string;
  routingId: string;
  tests: AccessPresetTest[];
  /** Refuse (422 DUPLICATE_TITLE) instead of replacing a preset with the same name. */
  createOnly?: boolean;
}

export function saveAccessPreset(payload: AccessPresetSavePayload): Promise<{ id: number }> {
  return apiClient.post<ApiResponse<{ id: number }>>('/access-orders/presets', payload).then((r) => r.data.data);
}

// ─── Access admin: preset management (labs_access_presets) ────────────────────

export interface AccessPresetRow {
  id: number;
  title: string;
  routingId: string;
  location: string;
  shared: boolean;
  testCount: number;
  owned: boolean;
}

export interface AccessPresetUpdateInput {
  title: string;
  shared: string;
  /** Sent together with `tests` to replace the preset's location and test list. */
  routingId?: string;
  tests?: AccessPresetTest[];
}

export function listAccessPresetsAdmin(): Promise<AccessPresetRow[]> {
  return apiClient.get<ApiResponse<AccessPresetRow[]>>('/access-orders/presets/manage').then((r) => r.data.data);
}

export function updateAccessPreset(id: number, payload: AccessPresetUpdateInput): Promise<{ success: boolean }> {
  return apiClient.put<ApiResponse<{ success: boolean }>>(`/access-orders/presets/${id}`, payload).then((r) => r.data.data);
}

export function deleteAccessPreset(id: number): Promise<void> {
  return apiClient.delete(`/access-orders/presets/${id}`).then(() => undefined);
}
